import React from "react";
import {
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type ButtonProps,
  type Omittable,
} from "@salestrak/ui";
import { formatAge } from "#app/lib/issue-utils";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { AGE_STATUS, AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useMinuteKey } from "#app/lib/hooks/use-date-boundary-key";
import {
  clockRunning,
  effectiveProjectCycle,
  formatDueLine,
  formatNextUpdate,
  formatUpdateCycle,
  liveAgeStatus,
  nextUpdateDue,
  type ProjectAge,
  type StagePin,
  type WorkflowCycle,
} from "#app/lib/project-updates";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

export type AgeLabelProps = Omittable<{
  age?: ProjectAge | null;
  /** The Project's Stage, so a Stage-inherited cycle resolves for the live
   *  color (looked up in workspace data — every issue shape carries the id).
   *  Omitted, Projects with their own cycle still compute live; inherited
   *  ones fall back to the stored status. */
  stageId?: string | null;
  /** Label size for BARE renders (a table cell, a drilldown). `null` opts a
   *  bare render nested inside an external button into inheriting that
   *  button's rung font — a 24px action carries a 12px label. Ignored for the
   *  label when `onClick` wraps the content in AgeLabel's own Button, which
   *  always inherits its rung the same way. */
  size?: "xs" | "sm" | null;
  buttonSize?: ButtonProps["size"];
  /** Paint of the press-wrapping Button. Ghost when omitted. */
  buttonPaint?: Pick<ButtonProps, "variant" | "color" | "ink">;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  /** Fixed text in place of the age count (e.g. "Update" where the press is
   *  the point). Renders as a quiet muted action button — no status tint (a
   *  colored action word reads as state, not an action). Pair with `tooltip`
   *  so the age and status stay reachable on hover. Ignored without `onClick`
   *  — inert action text is a dead affordance, so read-only renders keep the
   *  age count. */
  label?: string;
  /** Explain the label on hover: the status the color encodes, and when the
   *  Project was last updated (the clock counts from there). Opt-in — surfaces
   *  that already spell the status out beside the label don't need it. */
  tooltip?: boolean;
  /** Show a ghost pulse (muted icon, no count) where updates are off, instead
   *  of rendering nothing. Opt-in for surfaces where the updates slot is part
   *  of the card's grammar (the kanban card — every column, closed ones
   *  included); list rows and pickers keep absence as the state. */
  showOff?: boolean;
  /** Paint the status glyph beside the count. Default on. Off, a RUNNING clock
   *  reads as tinted text alone — the list view's grammar, where a column of
   *  pulses beside a column of counts says the same thing twice and the tint
   *  already carries the tier. The updates-off ghost (`showOff`) ignores
   *  this: with nothing counting, the glyph IS the cell. */
  glyph?: boolean;
  /** Tabular digits on the count, for a column of counts that must stack —
   *  a ledger gutter where "11d" and "20d" sit one above the other. */
  tabular?: boolean;
}>;

/** Where the updates-off ghost applies: the Project's effective cycle is off —
 *  whichever rung turned it off (the Project's own opt-out, a Stage off-pin,
 *  an off Workflow, or a closed Stage's default) — whatever status the column
 *  still carries. A Project whose cycle is ON but whose clock hasn't synced
 *  yet shows nothing — the live chip takes the slot when the status lands. */
export function showsUpdatesOffGhost(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): boolean {
  return effectiveProjectCycle(age, stage, workflow).cycle == null;
}

/** The `tooltip` mode's line, for a label that is a VALUE with no press of
 *  its own (a picker row): the deadline the color encodes ("3 months overdue"
 *  / "in 12 days"), the cycle, and when the clock last reset. A label that is
 *  a press carries no fact tooltip — its host names the action ("Update"),
 *  the way every row action's does; the chip itself is the fact. */
function ageTooltipText(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): string {
  const { cycle } = effectiveProjectCycle(age, stage, workflow);
  const deadline = nextUpdateDue(age, stage, workflow);
  const status = liveAgeStatus(age, stage, workflow) ?? age?.status;
  const lead = deadline
    ? formatNextUpdate(deadline)
    : (AGE_STATUS_CONFIG[status as AgeStatus] ?? AGE_STATUS_CONFIG.offTrack).label;
  return [
    lead,
    cycle ? formatUpdateCycle(cycle) : null,
    age?.date ? `Last update ${formatRelativeDate(age.date)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The tooltip a clock PRESS wears: the action, then the deadline the tint
 *  encodes — "Update · Due in 17 days". The chip's own count is the age, so
 *  the line does not restate it; the "Last update" sentence lives in the
 *  dialog the press opens. */
export function updateActionTooltip(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow?: WorkflowCycle | null,
): string {
  const deadline = nextUpdateDue(age, stage, workflow);
  return ["Update", deadline ? formatDueLine(deadline) : null].filter(Boolean).join(" · ");
}

/** `clockRunning` for a host holding only the Stage id — resolves the Stage
 *  and Workflow off workspace data the way AgeLabel does, so a table cell, a
 *  list row and the hover popover all answer the one question the same way. */
export function useClockRunning(
  age: ProjectAge | null | undefined,
  stageId: string | null | undefined,
): boolean {
  const { stageMap, workflowMap } = useWorkspaceData();
  const stage = stageId ? stageMap.get(stageId) : undefined;
  const workflow = stage?.workflowId ? workflowMap.get(stage.workflowId) : undefined;
  return clockRunning(age, stage, workflow);
}

const GHOST_PAINT: Pick<ButtonProps, "variant"> = { variant: "ghost" };

export const AgeLabel = React.memo(
  function AgeLabel({
    age,
    stageId,
    size = "sm",
    buttonSize = "xs",
    buttonPaint = GHOST_PAINT,
    onClick,
    label,
    tooltip,
    showOff,
    glyph = true,
    tabular,
  }: AgeLabelProps) {
    const { stageMap, workflowMap } = useWorkspaceData();
    // Re-buckets the color as time passes — the memo blocks parent-driven
    // re-renders, and cycles go down to hours, so the day key isn't enough.
    void useMinuteKey();
    // The color is computed LIVE from the last Update + effective cycle; the
    // stored status only moves on cron passes, so alone it can sit green on a
    // Project already lapsed. Stored remains the fallback when the cycle can't
    // be resolved here. Both maps come from context, so a Workflow default
    // change re-renders through the provider despite the memo.
    // `undefined` (not null) when the id is absent: clockRunning reads that as
    // "unknown" and trusts the stored status, the way the live color does.
    const stage = stageId ? stageMap.get(stageId) : undefined;
    const workflow = stage?.workflowId
      ? workflowMap.get(stage.workflowId)
      : undefined;
    const status = liveAgeStatus(age, stage, workflow) ?? age?.status;
    // Icon scales with the text size so smaller labels get a smaller glyph.
    const iconSize = size === "xs" ? "sm" : "md";

    // The press affordance is a Button, so the trigger has to be a wrapping Box
    // — a bare trigger would nest a button inside a button. The hover delay is
    // the app-wide group's (300ms): slow enough that sweeping the pointer down
    // a list of rows doesn't flash a tooltip per row, since the label is a
    // glanceable fact and the tooltip is for the deliberate "what does this
    // clock mean" hover.
    const withTooltip = (node: React.ReactNode, explanation: string) =>
      tooltip ? (
        <Tooltip>
          <TooltipTrigger render={<Box direction="row" align="center" />}>
            {node}
          </TooltipTrigger>
          <TooltipPopup side="top">{explanation}</TooltipPopup>
        </Tooltip>
      ) : (
        node
      );

    // The chip is its own action. Hosts seat it inside pressable rows (list
    // rows, table cells) whose press opens the entity, so the press stops
    // here — otherwise the entity opens under the posting dialog.
    const press = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onClick?.(e);
    };

    // Updates off (a Stage cycle off, a per-Project override, or a closed
    // Stage) — render nothing by default; absence is the state (the picker rows
    // already hide it, and the "No Follow up" filter option covers finding
    // these). Surfaces that opt in via showOff get the ghost instead where
    // updates were expected: a muted pulse, icon only — no count, because
    // nothing is counting. The kanban card wraps it as an Updates-popover
    // trigger (the compact cycle picker — start updates right there).
    if (!clockRunning(age, stage, workflow)) {
      if (!showOff || !showsUpdatesOffGhost(age, stage, workflow)) return null;
      const offConfig = AGE_STATUS_CONFIG.disabled;
      const ghost = (
        <Icon name={PROPERTY_ICON.followUp} size={iconSize} color={offConfig.decoColor} />
      );
      const ghostNode = onClick ? (
        <Button {...buttonPaint} size={buttonSize} onClick={press}>
          {ghost}
        </Button>
      ) : (
        ghost
      );
      return withTooltip(ghostNode, offConfig.label);
    }

    const actionLabel = onClick ? label : undefined;
    // The count is the AGE — time since the last update, the number reps
    // read on every Project. The tint carries the deadline; the tooltip
    // names both so the pair reads as one story.
    const formatted = actionLabel ?? formatAge(age?.date);
    if (!formatted) return null;

    // `decoColor` is the age status' palette hue — the SAME hue the age chart
    // slices paint (`use-dimension-counts.ts`), and one `color=` name keeps the
    // pulse glyph and the label on one value. The semantic pair
    // (`--destructive` glyph / `--destructive-foreground` text) splits them.
    const config =
      AGE_STATUS_CONFIG[status as AgeStatus] ?? AGE_STATUS_CONFIG.offTrack;

    const content = actionLabel ? (
      // Direct Button children so the icon/label spacing is the button's own
      // gap — the rung every profile-header Update button sits on.
      <>
        <Icon name={PROPERTY_ICON.followUp} size="sm" color="muted" />
        <Text size={size} color="muted">
          {formatted}
        </Text>
      </>
    ) : (
      <Box direction="row" gap="xs" align="center">
        {glyph ? (
          <Icon name={config.icon} size={iconSize} color={config.decoColor} />
        ) : null}
        {/* Inside a button the label inherits the button rung's font
          * (`size={null}`), so a 24px action carries a 12px label. The two
          * attention tiers paint glyph and count in one hue; Upcoming keeps
          * the green on the glyph alone and its count reads muted — a clock
          * that needs nothing sits at the weight of the quiet facts around
          * it, so a board of healthy Projects is not a board of green. */}
        {status === AGE_STATUS.onTrack ? (
          <Text size={onClick ? null : size} tabular={tabular} variant="muted">
            {formatted}
          </Text>
        ) : (
          <Text size={onClick ? null : size} tabular={tabular} color={config.decoColor}>
            {formatted}
          </Text>
        )}
      </Box>
    );

    const node = onClick ? (
      <Button {...buttonPaint} size={buttonSize} onClick={press}>
        {content}
      </Button>
    ) : (
      content
    );

    return withTooltip(node, ageTooltipText(age, stage, workflow));
  },
  (prev, next) =>
    prev.size === next.size &&
    prev.buttonSize === next.buttonSize &&
    prev.buttonPaint?.variant === next.buttonPaint?.variant &&
    prev.buttonPaint?.color === next.buttonPaint?.color &&
    prev.buttonPaint?.ink === next.buttonPaint?.ink &&
    prev.label === next.label &&
    prev.tooltip === next.tooltip &&
    prev.showOff === next.showOff &&
    prev.glyph === next.glyph &&
    prev.tabular === next.tabular &&
    prev.age?.date === next.age?.date &&
    prev.age?.status === next.age?.status &&
    prev.age?.updatesOff === next.age?.updatesOff &&
    prev.age?.cycle?.amount === next.age?.cycle?.amount &&
    prev.age?.cycle?.step === next.age?.cycle?.step &&
    prev.stageId === next.stageId &&
    prev.onClick === next.onClick,
);
